import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { AlignType } from "./enums";
import { IComponent, ILink } from "./interfaces";
import { IPicassoState, ScrollPosition, SelectablePoint } from "./types";

/**
 * Vrací informaci o tom, zdali je komponenta o daném {@link componentIndex} označená či nikoliv.
 * @param state
 * @param componentIndex Index komponenty
 */
export const isComponentSelected: Selector<IPicassoState, boolean, number> = (state, componentIndex) => state.selectedComponentsIndexes.includes(componentIndex);

/**
 * Vrací informaci o tom, zdali je spojení dané {@link linkIndex} označená či nikoliv.
 * @param state
 * @param linkIndex Index spojení
 */
export const isLinkSelected: Selector<IPicassoState, boolean, number> = (state, linkIndex) => state.selectedLinksIndexes.includes(linkIndex);

/**
 * Vrací informaci o tom, zdali je nějaký bod spojení označen či nikoliv.
 * @param state
 * @param linkIndex Index spojení
 */
export const isAnyPointSelected: Selector<IPicassoState, boolean, number> = (state, linkIndex) => state.selectedPoint != null && state.selectedPoint.linkIndex === linkIndex;

/**
 * Vrací komponentu dle poskytnutého {@link componentIndex}.
 * @param state
 * @param componentIndex Index komponenty
 */
export const getComponent: Selector<IPicassoState, IComponent, number> = (state, componentIndex) => state.components[componentIndex];

/**
 * Vrací komponentu dle poskytnutého {@link id}.
 * @param state
 * @param id ID komponenty.
 */
export const getComponentById: Selector<IPicassoState, IComponent | undefined, string> = (state, id) => state.components.find(x => x.id === id);

/** Vrací pole všech komponent. */
export const getComponents: Selector<IPicassoState, readonly IComponent[]> = (state) => state.components;

/**
 * Vrací spojení dle poskytnutého {@link linkIndex}
 * @param state
 * @param linkIndex Index spojení
 */
export const getLink: Selector<IPicassoState, ILink, number> = (state, linkIndex) => state.links[linkIndex];

/** Vrací pole všech spojení. */
export const getLinks: Selector<IPicassoState, readonly ILink[]> = (state) => state.links;

/** Vrací pole indexů všech označených komponent. */
export const getSelectedComponentsIndexes: Selector<IPicassoState, readonly number[]> = (state) => state.selectedComponentsIndexes;

/** Vytvoří selector pro získání označených komponent. */
export const createGetSelectedComponents = () => createSelector(
    getSelectedComponentsIndexes,
    getComponents,
    (selectedComponentsIndexes, components) => selectedComponentsIndexes.map(index => components[index]),
);

/** Vrací pole indexů všech označených spojení. */
export const getSelectedLinksIndexes: Selector<IPicassoState, readonly number[]> = (state) => state.selectedLinksIndexes;

/** Vytvoří selector pro získání označených spojení. */
export const createGetSelectedLinks = () => createSelector(
    getSelectedLinksIndexes,
    getLinks,
    (selectedLinksIndexes, links) => selectedLinksIndexes.map(index => links[index]),
);

/** Vrací označený bod spojení. */
export const getSelectedPoint: Selector<IPicassoState, SelectablePoint | null> = (state) => state.selectedPoint;

/** Vrací aktuální úroveň přiblížení plátna picassa. */
export const getScaleIndex: Selector<IPicassoState, number> = (state) => state.scaleIndex;

/** Vrací vyžadovanou pozici scrollbarů plátna picassa. */
export const getRequestedScrollPosition: Selector<IPicassoState, ScrollPosition> = (state) => state.requestedScrollPosition;

/**
 * Vrací informaci o viditelnosti dané komponenty.
 * @param state Stav picassa.
 * @param componentIndex Index komponenty, pro kterou se má zjišťovat viditelnost.
 */
export const isComponentVisible: Selector<IPicassoState, boolean, number> = (state, componentIndex) => getComponent(state, componentIndex).isVisible;

/**
 * Vytvoří cachovaný selector vracející šablonu gridu pro renderovanou komponentu picasso komponenty.
 * @param state Stav picassa.
 * @param picassoIndex Index komponenty.
 * @returns CSS šablonu gridu.
 */
export const createGetGridTemplate = () => createSelector(
    getComponents,
    (_state: IPicassoState, picassoIndex: number) => picassoIndex,
    (components, picassoIndex) => {
        const component = components[picassoIndex];
        const columns = { leftAutos: "", rightAutos: "", topAutos: "", bottomAutos: "" };

        for (const childIndex of component.children) {
            const childComponent = components[childIndex];

            if (childComponent.align === AlignType.left)
                columns.leftAutos += "auto ";
            else if (childComponent.align === AlignType.right)
                columns.rightAutos += " auto";
            else if (childComponent.align === AlignType.top)
                columns.topAutos += "auto ";
            else if (childComponent.align === AlignType.bottom)
                columns.bottomAutos += " auto";
        }

        return `${columns.topAutos}1fr${columns.bottomAutos} / ${columns.leftAutos}1fr${columns.rightAutos}`;
    },
);

/**
 * Vytvoří cachovaný selector vracející číslo sloupce gridu, ve kterém se má picasso komponenta zobrazit.
 * @param state Stav picassa.
 * @param picassoIndex Index komponenty.
 * @returns Číslo sloupce.
 */
export const createGetColumnNumber = () => createSelector(
    getComponents,
    (_state: IPicassoState, picassoIndex: number) => picassoIndex,
    (components, picassoIndex) => {
        const component = components[picassoIndex];

        if (component.parent === null || component.align === AlignType.none)
            return 0;

        const parentComponent = components[component.parent];
        const siblings = parentComponent.children.map(x => components[x]);

        if (component.align === AlignType.left)
            return siblings.filter(x => x.align === AlignType.left).indexOf(component) + 1; // +1 protože row začínají od jedničky

        if (component.align === AlignType.client)
            return siblings.filter(x => x.align === AlignType.left).length + 1; // +1 protože row začínají od jedničky

        if (component.align !== AlignType.right)
            return siblings.filter(x => x.align === AlignType.left || x.align === AlignType.right).length + 1 + 1; // +1 protože rows začínají od jedničky, + 1 protože alClient

        const numberOfLeftColumns = siblings.filter(x => x.align === AlignType.left).length + 1 + 1; // +1 protože rows začínají od jedničky, + 1 protože alClient
        return numberOfLeftColumns + siblings.filter(x => x.align === AlignType.right).indexOf(component);
    },
);

/**
 * Vytvoří cachovaný selector vracející číslo řádku gridu, ve kterém se má picasso komponenta zobrazit.
 * @param state Stav picassa.
 * @param picassoIndex Index komponenty.
 * @returns Číslo řádku.
 */
export const createGetRowNumber = () => createSelector(
    getComponents,
    (_state: IPicassoState, picassoIndex: number) => picassoIndex,
    (components, picassoIndex) => {
        const component = components[picassoIndex];

        if (component.parent === null || component.align === AlignType.none)
            return 0;

        const parentComponent = components[component.parent];
        const siblings = parentComponent.children.map(x => components[x]);

        if (component.align === AlignType.top)
            return siblings.filter(x => x.align === AlignType.top).indexOf(component) + 1; // +1 protože rows začínají od jedničky

        if (component.align !== AlignType.bottom)
            return siblings.filter(x => x.align === AlignType.top).length + 1; // +1 protože rows začínají od jedničky

        const numberOfTopColumns = siblings.filter(x => x.align === AlignType.top).length + 1 + 1; // +1 protože rows začínají od jedničky, + 1 protože alClient
        return numberOfTopColumns + siblings.filter(x => x.align === AlignType.bottom).indexOf(component);
    },
);
